import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  destroySttTokenTimeout,
  pauseRecording,
  resumeRecording,
  saveDraft,
  startRecording,
  stopRecording,
} from "redux/actions/recognizerActions";
import { constants } from "redux/constants/redux-constants";
import {
  checkIsDraftComposition,
  formatComposition,
} from "screens/patientInfo/helpers/draftCheckHelper";
import { recognizerManager } from "screens/patientInfo/classes/RecognizerManager";
import { set_current_events } from "redux/actions/ScribeAcitons";
import { getCurrentRecordingState } from "config/helpers/recordingStateSelector";
import { addActiveSOAPGeneration, removeActiveSOAPGeneration } from "redux/actions/activeSOAPGenerationActions";

const useSpeechToText = () => {
  const dispatch = useDispatch();
  const encounterObj = useSelector((state) => state?.encounterObj);
  const patientObj = useSelector((state) => state?.patientObj?.patientObj);
  const { recording, paused, conversationId } = useSelector((state) =>
    getCurrentRecordingState(
      state,
      patientObj?.id,
      encounterObj?.encounter?.id
    )
  );
  const currentEvents = useSelector((state) => state?.scribe?.currentEvents);

  const [transcribingText, setTranscribingText] = useState(null);
  const [transcribedText, setTranscribedText] = useState(
    formatComposition(checkIsDraftComposition())
  );
  const [recognitionEvents, setRecognitionEvents] = useState([]);

  useEffect(() => {
    //* When encounterObj empties from start new encounter need to remove the draft composition
    if (!encounterObj?.encounter?.id && !recording && !paused)
      setTranscribedText([]);
  }, [encounterObj]);

  const handleTranscribing = (e) => {
    const { text } = e.result;
    setTranscribingText(text);
  };

  const handleTranscribed = (e) => {
    const { text } = e.result;
    setTranscribingText(null);
    if (text) {
      setTranscribedText((prev) => [...prev, text]);
      const newEvent = {
        ...JSON.parse(e.result.json),
        sessionId: e.result.resultId,
        timestamp: Date.now(),
      };

      console.log("transcribed_event", e);
      console.log("transcribed_event_result", e?.result);

      setRecognitionEvents((prev) => [...prev, newEvent]);
    }
  };

  useEffect(() => {
    dispatch(set_current_events([...recognitionEvents]));
  }, [recognitionEvents]);

  const initiateTranscription = () => {
    dispatch(
      startRecording({
        handleTranscribing,
        handleTranscribed,
      })
    );
  };

  const stopTranscription = (context) => {
    dispatch(set_current_events([]));
    dispatch(
      stopRecording({
        recognitionEvents,
        setRecognitionEvents,
        conversationId,
        context,
      })
    );
    setTranscribingText("");
    setTranscribedText([]);
  };

  const pauseTranscription = async (setContextPopup) => {
    if (typeof setContextPopup === "function") setContextPopup(true);

    const RecognitionEvents =
      recognitionEvents?.length > 0 ? recognitionEvents : currentEvents;
    await dispatch(
      pauseRecording({
        recognitionEvents: RecognitionEvents,
        setRecognitionEvents,
        conversationId,
      })
    );

    dispatch(set_current_events([]));
  };

  const stopRecognizer = () => {
    dispatch(set_current_events([]));

    recognizerManager.stopTranscription();
    destroySttTokenTimeout();

    dispatch({
      type: constants.RECOGNIZER.RESET_PROGRESS_STATE,
      payload: { patientId: patientObj?.id, encounterId: encounterObj?.encounter?.id },
    });

    dispatch({
      type: constants.RECOGNIZER.STOP_RECORDING,
      payload: { patientId: patientObj?.id, encounterId: encounterObj?.encounter?.id },
    });

    dispatch({
      type: constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER,
    });
    dispatch(removeActiveSOAPGeneration({
      patientId: patientObj?.id,
      encounterId: encounterObj?.encounter?.id,
    }));
  };

  const resumeTranscription = async (compositionId = conversationId) => {
    dispatch(set_current_events([]));
    if (compositionId === conversationId)
      await dispatch(
        resumeRecording({
          recognitionEvents,
          setRecognitionEvents,
          conversationId,
        })
      );
    else
      await dispatch(
        resumeRecording({
          recognitionEvents,
          setRecognitionEvents,
          conversationId: compositionId,
          handleTranscribed,
          handleTranscribing,
        })
      );
    dispatch({
      type: constants.RECOGNIZER.START_RECORDING,
      payload: {
        patientId: patientObj?.id,
        encounterId: encounterObj?.encounter?.id,
        conversationId: compositionId,
      },
    });
    dispatch(
      addActiveSOAPGeneration({
        patientId: patientObj?.id,
        encounterId: encounterObj?.encounter?.id,
        patientName: patientObj?.name?.[0]?.text,
      })
    );
  };

  const saveDraftTranscription = () => {
    dispatch(set_current_events([]));
    dispatch(
      saveDraft({
        recognitionEvents,
        setRecognitionEvents,
        conversationId,
      })
    );
  };
  return {
    transcribingText,
    transcribedText,
    recognitionEvents,
    setRecognitionEvents,
    controls: {
      initiateTranscription,
      stopTranscription,
      pauseTranscription,
      resumeTranscription,
      saveDraftTranscription,
      stopRecognizer,
    },
  };
};

export default useSpeechToText;